import logo from '@/assets/images/logo.svg';
/**
 * @spec docs/specs/features/staking-position-ui.md#privy-configuration
 * Privy SDK configuration for Phase 2 (authenticated transactions)
 */
import type { PrivyClientConfig } from '@privy-io/react-auth';
import { env } from './env';
import { getStakingChainId, CHAIN_CONFIG } from './chains';

// Privy App ID (must reuse from main platform for unified accounts)
export const PRIVY_APP_ID = env.privyAppId;

// Determine which chains to use based on environment
const stakingChainId = getStakingChainId();
const isTestnet = stakingChainId === 84532; // Base Sepolia

// Chain definitions for Privy (matching viem/chains format)
const baseMainnet = {
  id: CHAIN_CONFIG.BASE.id,
  name: CHAIN_CONFIG.BASE.name,
  network: 'base',
  nativeCurrency: CHAIN_CONFIG.BASE.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.BASE.rpcUrl || 'https://mainnet.base.org'] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.BASE.explorerName,
      url: CHAIN_CONFIG.BASE.explorerUrl || 'https://basescan.org',
    },
  },
};

const baseSepoliaChain = {
  id: CHAIN_CONFIG.BASE_SEPOLIA.id,
  name: CHAIN_CONFIG.BASE_SEPOLIA.name,
  network: 'base-sepolia',
  nativeCurrency: CHAIN_CONFIG.BASE_SEPOLIA.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.BASE_SEPOLIA.rpcUrl || 'https://sepolia.base.org'] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.BASE_SEPOLIA.explorerName,
      url: CHAIN_CONFIG.BASE_SEPOLIA.explorerUrl || 'https://sepolia.basescan.org',
    },
  },
};

const polygonMainnet = {
  id: CHAIN_CONFIG.POLYGON.id,
  name: CHAIN_CONFIG.POLYGON.name,
  network: 'polygon',
  nativeCurrency: CHAIN_CONFIG.POLYGON.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.POLYGON.rpcUrl || 'https://polygon-rpc.com'] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.POLYGON.explorerName,
      url: CHAIN_CONFIG.POLYGON.explorerUrl || 'https://polygonscan.com',
    },
  },
};

const polygonAmoyChain = {
  id: CHAIN_CONFIG.POLYGON_AMOY.id,
  name: CHAIN_CONFIG.POLYGON_AMOY.name,
  network: 'polygon-amoy',
  nativeCurrency: CHAIN_CONFIG.POLYGON_AMOY.nativeCurrency,
  rpcUrls: {
    default: { http: [CHAIN_CONFIG.POLYGON_AMOY.rpcUrl || 'https://rpc-amoy.polygon.technology'] },
  },
  blockExplorers: {
    default: {
      name: CHAIN_CONFIG.POLYGON_AMOY.explorerName,
      url: CHAIN_CONFIG.POLYGON_AMOY.explorerUrl || 'https://amoy.polygonscan.com',
    },
  },
};

// Supported chains for this app
const supportedChains = isTestnet
  ? [baseSepoliaChain, polygonAmoyChain]
  : [baseMainnet, polygonMainnet];

// Default chain is Base (mainnet or testnet depending on environment)
const defaultChain = isTestnet ? baseSepoliaChain : baseMainnet;

// Privy configuration - Phase 2: Full authentication support
export const privyConfig: PrivyClientConfig = {
  // Phase 2: Enable social auth methods alongside wallet connection
  loginMethods: ['email', 'wallet'],

  // Wallet appearance and supported wallets
  appearance: {
    theme: 'dark',
    accentColor: '#a046d3', // Brand purple
    logo: logo,
    walletList: ['metamask', 'coinbase_wallet', 'wallet_connect', 'rainbow', 'phantom'],
  },

  // Phase 2: Enable embedded wallet for users without external wallets
  embeddedWallets: {
    createOnLogin: 'users-without-wallets',
  },

  // Default chain: Base (wallet will auto-switch on connect)
  defaultChain,
  supportedChains,

  // Default storage (localStorage) - works for same-origin deployment
  // For cross-subdomain, see Q4 in spec for cookie configuration
};

/**
 * Cross-subdomain configuration (for future reference)
 *
 * For cross-subdomain session sharing:
 * storage: {
 *   type: 'cookie',
 *   cookieOptions: {
 *     domain: '.phenompoker.com',
 *     secure: true,
 *     sameSite: 'lax',
 *   },
 * }
 */
